import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './AutoJobApply.css';
import ProductSidebar from './ProductSidebar';
import { useToast } from './Toast/ToastProvider';
import LiveTab from './AutoJobApply/LiveTab';
import QueueTab from './AutoJobApply/QueueTab';

// Login-less ATS platforms run via the server-side Puppeteer agent.
// Everything else runs via the Chrome extension (when installed) so it uses
// the user's real, logged-in browser session.
const SERVER_PLATFORMS = new Set(['greenhouse', 'lever']);

const AutoJobApply = ({ embedded = false } = {}) => {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const toast = useToast();

  // Redirect to home if the session is invalid (e.g. logged out on another
  // device — the verify-token call clears localStorage and sets user=null).
  useEffect(() => {
    if (embedded) return; // parent landing handles un-auth UX
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate, embedded]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ applied: 0, skipped: 0, failed: 0 });
  const [wsConnected, setWsConnected] = useState(false);
  const [showLinkedInWarning, setShowLinkedInWarning] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [resumePath, setResumePath] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [canStart, setCanStart] = useState(false);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const consoleEndRef = useRef(null);
  const consoleBodyRef = useRef(null);
  const extSessionIdRef = useRef(null);
  const savedJobKeysRef = useRef(new Set()); // tracks jobs already persisted to DB

  // ── Approval queue (AIApply-style) — minimal UI for testing ──
  const [queueJobs, setQueueJobs] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueRefreshing, setQueueRefreshing] = useState(false);
  const [queueStatusFilter, setQueueStatusFilter] = useState('waiting');
  const [queueMode, setQueueMode] = useState('review');
  const [queueThreshold, setQueueThreshold] = useState(70);
  // Usage / plan state — shown above the queue table.
  const [usage, setUsage] = useState(null); // { plan, applies24_7:{used,limit}, resumeTailoring:{used,limit}, resetsInSeconds, queuePaused, approvedAwaiting }
  const [resetCountdown, setResetCountdown] = useState(0);

  // Active tab on the run/results step. Splits the experience into the two
  // clearly-different products: live browser-extension automation (LinkedIn,
  // Indeed, Dice, Monster) vs the server-side 24/7 background queue
  // (Greenhouse, Lever, Workable, Ashby).
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'queue'
  // Per-lane active flags. Set true in handleLaunch when that lane is launched;
  // set false in the lane's completion handler. UI flips to idle only when both
  // are false. Per-lane gating prevents stale EXT_UPDATE / ws events (from a
  // previous session or a passively-listening extension) from finishing a lane
  // we never started.
  const extLaneActiveRef = useRef(false);
  const serverLaneActiveRef = useRef(false);
  const extStatsRef = useRef({ applied: 0, skipped: 0, failed: 0 });
  const serverStatsRef = useRef({ applied: 0, skipped: 0, failed: 0 });

  // Each lane writes its own running totals to its ref; displayed stats are the sum.
  const recomputeMergedStats = useCallback(() => {
    setStats({
      applied: (extStatsRef.current.applied || 0) + (serverStatsRef.current.applied || 0),
      skipped: (extStatsRef.current.skipped || 0) + (serverStatsRef.current.skipped || 0),
      failed:  (extStatsRef.current.failed  || 0) + (serverStatsRef.current.failed  || 0),
    });
  }, []);

  const finishExtLane = useCallback(() => {
    if (!extLaneActiveRef.current) return;
    extLaneActiveRef.current = false;
    if (!serverLaneActiveRef.current) setIsRunning(false);
  }, []);

  const finishServerLane = useCallback(() => {
    if (!serverLaneActiveRef.current) return;
    serverLaneActiveRef.current = false;
    if (!extLaneActiveRef.current) setIsRunning(false);
  }, []);

  const getApiBase = () => {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env) return env;
    const origin = window.location.origin;
    if (origin.startsWith('http://') || origin.startsWith('https://')) return origin;
    return 'http://localhost:5000'; // fallback for Electron
  };
  const API_BASE_URL = getApiBase();
  const WS_URL = API_BASE_URL.replace(/^http/, 'ws');
  
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [profileResume, setProfileResume] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
const [extInstalled, setExtInstalled] = useState(false);
  

  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
    portfolio: '',
    currentTitle: '',
    yearsExperience: '',
    skills: '',
    professionalSummary: '',
    noticePeriod: 'Immediate',
    expectedSalary: '',
  });

  const [preferences, setPreferences] = useState({
    jobTitles: '',
    locations: '',
    jobType: 'Full-time',
    remote: 'Any',
    minSalary: '',
    excludeCompanies: '',
    keywords: '',
    platforms: { linkedin: true, indeed: false, dice: false, monster: false },
    maxApplications: '20',
    // OFF by default — free users can't use AI tailoring (returns 403 on
    // every call), and even paid users should opt in explicitly so they
    // know they're spending tokens.
    autoTailor: false,
  });

  // Detect Chrome extension
  useEffect(() => {
    const onReady = () => { setExtInstalled(true); };
    window.addEventListener('terview-ext-ready', onReady);
    // Ping in case bridge already loaded
    window.postMessage({ terviewExt: true, type: 'EXT_PING' }, '*');
    const onPong = (e) => { if (e.data?.terviewPage && e.data.type === 'EXT_PONG') onReady(); };
    window.addEventListener('message', onPong);
    // Also check DOM attribute
    if (document.documentElement.getAttribute('data-terview-ext')) onReady();
    return () => {
      window.removeEventListener('terview-ext-ready', onReady);
      window.removeEventListener('message', onPong);
    };
  }, []);

  // Listen for live updates from extension
  useEffect(() => {
    if (!extInstalled) return;
    const handler = async (e) => {
      if (!e.data?.terviewPage || e.data.type !== 'EXT_UPDATE') return;
      // Only process extension updates if we launched the ext lane this session
      // OR the extension is actively running (sync-up case after a page reload).
      // Without this gate, a passively-listening extension can flush its stale
      // logs/stats into the UI and clobber the server lane's output.
      if (!extLaneActiveRef.current && !e.data.running) return;

      // Sync UI to extension state — if extension is running but UI thinks it isn't,
      // flip the button to "Stop Agent" so the user can control it.
      if (e.data.running && !isRunning) {
        extLaneActiveRef.current = true;
        setIsRunning(true);
      }

      extStatsRef.current = {
        applied: e.data.stats?.applied || 0,
        skipped: e.data.stats?.skipped || 0,
        failed:  e.data.stats?.failed  || 0,
      };
      recomputeMergedStats();
      if (e.data.logs?.length) {
        setLogs(e.data.logs.map(l => ({ message: l.message, type: l.type, time: l.time })));
      }
      if (e.data.appliedJobs !== undefined) {
        const jobs = (e.data.appliedJobs || []).filter(j => j.status !== 'skipped');
        setAppliedJobs(jobs.map(j => ({
          title: j.title,
          company: j.company,
          location: j.location,
          platform: j.platform,
          status: j.status,
          appliedAt: j.appliedAt,
          logoUrl: j.logoUrl || null,
          postedDate: j.postedDate || '',
          salary: j.salary || '',
          employmentType: j.employmentType || '',
          description: j.description || '',
        })));

        // Persist new finalized jobs to DB
        if (extSessionIdRef.current) {
          const token = localStorage.getItem('token');
          for (const j of jobs) {
            if (j.status === 'applying') continue;
            const key = `${j.title}|${j.platform}|${j.appliedAt}`;
            if (savedJobKeysRef.current.has(key)) continue;
            savedJobKeysRef.current.add(key);
            fetch(`${API_BASE_URL}/api/auto-apply/ext-job`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ sessionId: extSessionIdRef.current, job: j })
            }).catch(() => {});
          }
        }
      }
      // Session finished — save to DB, then reset UI after a short delay
      if (!e.data.running && isRunning) {
        setAppliedJobs(prev => prev.map(j => j.status === 'applying' ? { ...j, status: 'failed' } : j));
        if (extSessionIdRef.current) {
          fetch(`${API_BASE_URL}/api/auto-apply/ext-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ sessionId: extSessionIdRef.current, stats: e.data.stats })
          }).catch(() => {});
          extSessionIdRef.current = null;
        }
        finishExtLane();

      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [extInstalled, isRunning]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRunning) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRunning]);

  const [showNavBlockModal, setShowNavBlockModal] = useState(false);
  const [pendingNavUrl, setPendingNavUrl] = useState(null);

  useEffect(() => {
    if (!isRunning) return;
    const handleClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto')) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavUrl(href);
      setShowNavBlockModal(true);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isRunning]);

  useEffect(() => {
    if (consoleBodyRef.current) {
      consoleBodyRef.current.scrollTop = consoleBodyRef.current.scrollHeight;
    }
  }, [logs]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.isRunning) { setIsRunning(true); connectWebSocket(); }
      } catch {}
    };
    if (user) checkStatus();
  }, [user]);

  useEffect(() => {
    setCanStart(false);
  }, [preferences.platforms]);

  useEffect(() => {
    const fetchProfileResume = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {

          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.profile) {
          const filename = data.profile.resumeFilename || null;
          setProfileResume(filename);
          if (filename) {
            setResumeChoice('profile');
            setResumePath(`uploads/resumes/${filename}`);
            setResumeName(filename);
          }
          setProfile(prev => ({
            ...prev,
            fullName:           data.profile.fullName           || prev.fullName,
            email:              data.profile.email              || prev.email,
            phone:              data.profile.phone              || prev.phone,
            location:           data.profile.location           || prev.location,
            linkedIn:           data.profile.linkedIn           || prev.linkedIn,
            portfolio:          data.profile.portfolio          || prev.portfolio,
            currentTitle:       data.profile.jobTitle           || prev.currentTitle,
            yearsExperience:    data.profile.yearsExperience    || prev.yearsExperience,
            skills:             data.profile.skills             || prev.skills,
            professionalSummary: data.profile.professionalSummary || prev.professionalSummary,
          }));
          // Hydrate queue settings from saved profile
          if (data.profile.autoApplyMode) setQueueMode(data.profile.autoApplyMode);
          if (typeof data.profile.matchThreshold === 'number') setQueueThreshold(data.profile.matchThreshold);
        }
      } catch {}
    };
    if (user) fetchProfileResume();
  }, [user]);

  const addClientLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
  };

  // ── Queue API helpers ──
  const fetchQueue = useCallback(async (statusOverride) => {
    const status = statusOverride ?? queueStatusFilter;
    setQueueLoading(true);
    try {
      const url = `${API_BASE_URL}/api/auto-apply/queue${status ? `?status=${status}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      if (res.ok) setQueueJobs(data.jobs || []);
    } catch (err) {
      console.error('fetchQueue:', err.message);
    } finally {
      setQueueLoading(false);
    }
  }, [API_BASE_URL, queueStatusFilter]);

  const refreshQueue = async () => {
    setQueueRefreshing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (res.ok) {
        if (data.capped) {
          toast.warning(`Discovery cap reached (${data.capUsed}/${data.capLimit}). Resets at 00:00 UTC.`);
        } else if (data.stoppedAtCap) {
          toast.success(`Found ${data.discovered} new jobs · ${data.autoApproved} auto-approved (capped at daily limit)`);
        } else if (data.discovered === 0) {
          toast.info('No new jobs since last refresh.');
        } else {
          toast.success(`Found ${data.discovered} new jobs${data.autoApproved ? ` · ${data.autoApproved} auto-approved` : ''}`);
        }
      } else {
        toast.error(`Discovery failed: ${data.message}`);
      }
    } catch (err) {
      toast.error(`Discovery error: ${err.message}`);
    } finally {
      setQueueRefreshing(false);
      fetchQueue();
      fetchUsage();
    }
  };

  // Fetch usage + plan + reset countdown. Called on mount, after each
  // refresh/approve/reject, and after pause-toggle.
  const fetchUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/usage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsage(data);
        setResetCountdown(data.resetsInSeconds || 0);
      }
    } catch (err) {
      console.error('fetchUsage:', err.message);
    }
  }, [API_BASE_URL]);

  // Tick the countdown every second so the "resets in 8h 23m" label stays live.
  useEffect(() => {
    if (!resetCountdown) return;
    const id = setInterval(() => setResetCountdown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resetCountdown]);

  // Load usage on mount + whenever the queue list changes.
  useEffect(() => {
    if (user) fetchUsage();
  }, [user, fetchUsage]);

  const togglePause = async () => {
    if (!usage) return;
    try {
      await fetch(`${API_BASE_URL}/api/auto-apply/queue/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ paused: !usage.queuePaused }),
      });
      fetchUsage();
    } catch (err) {
      addClientLog(`❌ Pause toggle error: ${err.message}`, 'error');
    }
  };

  const decideJob = async (id, action) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/${id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(`${action === 'approve' ? 'Approve' : 'Reject'} failed: ${data.message}`);
      } else if (action === 'approve') {
        toast.success('Approved — applying in the background', { duration: 3500 });
      }
    } catch (err) {
      toast.error(`${action} error: ${err.message}`);
    } finally {
      fetchQueue();
      fetchUsage();
    }
  };

  const [queueSettingsSavedAt, setQueueSettingsSavedAt] = useState(null);
  const saveQueueSettings = async (mode, threshold) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/queue/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ mode, threshold }),
      });
      if (res.ok) {
        setQueueSettingsSavedAt(Date.now());
        setTimeout(() => setQueueSettingsSavedAt(null), 2000);
      }
    } catch (err) {
      addClientLog(`❌ Settings save error: ${err.message}`, 'error');
    }
  };

  // Load queue on mount + whenever the status filter changes
  useEffect(() => {
    if (user) fetchQueue();
  }, [user, queueStatusFilter, fetchQueue]);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/ws/auto-apply?token=${token}`);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          setLogs(prev => [...prev, { message: data.message, type: data.level || 'info', time: data.time }]);
        } else if (data.type === 'job_result') {
          setAppliedJobs(prev => [...prev, data.job]);
          serverStatsRef.current[data.job.status] = (serverStatsRef.current[data.job.status] || 0) + 1;
          recomputeMergedStats();
        } else if (data.type === 'complete') {
          if (data.stats) {
            serverStatsRef.current = {
              applied: data.stats.applied || 0,
              skipped: data.stats.skipped || 0,
              failed:  data.stats.failed  || 0,
            };
            recomputeMergedStats();
          }
          setLogs(prev => [...prev, { message: '🎯 Server lane completed!', type: 'success', time: new Date().toLocaleTimeString() }]);
          finishServerLane();
        } else if (data.type === 'error') {
          setLogs(prev => [...prev, { message: `❌ ${data.message}`, type: 'error', time: new Date().toLocaleTimeString() }]);
          finishServerLane();
        }
      } catch {}
    };
    ws.onclose = () => { setWsConnected(false); wsRef.current = null; };
    ws.onerror = () => setWsConnected(false);
  }, [WS_URL]);

  useEffect(() => () => { if (wsRef.current) wsRef.current.close(); }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/upload-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) { setResumePath(data.resumePath); setResumeName(file.name); }
      else addClientLog(`❌ Upload failed: ${data.message}`, 'error');
    } catch (err) {
      addClientLog(`❌ Upload error: ${err.message}`, 'error');
    }
  };

  const handleLaunch = async () => {
    const errors = {};
    if (!preferences.jobTitles.trim()) errors.jobTitles = true;
    if (!preferences.locations.trim()) errors.locations = true;
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.jobTitles) addClientLog('⚠️ Enter at least one job title', 'warning');
      if (errors.locations) addClientLog('⚠️ Enter at least one location', 'warning');
      return;
    }
    if (!Object.values(preferences.platforms).some(Boolean)) { addClientLog('⚠️ Select at least one platform', 'warning'); return; }

    // Split selected platforms into two lanes:
    //   serverPlatforms — login-less ATS (Greenhouse, Lever) → backend Puppeteer
    //   extPlatforms    — everything else → Chrome extension (real browser session)
    const serverPlatforms = {};
    const extPlatforms = {};
    for (const [k, v] of Object.entries(preferences.platforms)) {
      if (SERVER_PLATFORMS.has(k)) serverPlatforms[k] = v;
      else extPlatforms[k] = v;
    }
    const extEnabled = Object.values(extPlatforms).some(Boolean);
    const serverEnabled = Object.values(serverPlatforms).some(Boolean);

    setIsRunning(true);
    setLogs([]);
    setAppliedJobs([]);
    setStats({ applied: 0, skipped: 0, failed: 0 });
    extStatsRef.current = { applied: 0, skipped: 0, failed: 0 };
    serverStatsRef.current = { applied: 0, skipped: 0, failed: 0 };
    extLaneActiveRef.current = false;
    serverLaneActiveRef.current = false;

    // Local counter — used only to gate the "no lane launched" warning at the
    // bottom of this function. Distinct from the active-flag refs, which are
    // mutated by completion handlers and can flip back to false while we're
    // still mid-launch.
    let lanesLaunched = 0;

    // ── Lane 1: Chrome extension (LinkedIn / Indeed / Dice / Monster) ──
    if (extInstalled && extEnabled) {
      extLaneActiveRef.current = true;
      lanesLaunched++;
      addClientLog('⚡ Extension lane: launching for ' + Object.keys(extPlatforms).filter(k => extPlatforms[k]).join(', '), 'info');
      savedJobKeysRef.current = new Set();
      extSessionIdRef.current = null;

      // Force-disable AI Tailoring for any user whose plan can't actually
      // use it. Otherwise the extension fires /tailor-resume on every job,
      // gets a 403, logs a warning, and falls back — wasting time + cluttering
      // the console. The toggle is also gated in the UI for free users, but
      // this is the runtime safety net.
      const tailoringAllowed = (usage?.resumeTailoring?.limit ?? 0) > 0;
      const safePreferences = {
        ...preferences,
        platforms: extPlatforms,
        autoTailor: tailoringAllowed && preferences.autoTailor,
      };

      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/ext-start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ preferences: safePreferences, profile })
        });
        const data = await res.json();
        if (data.sessionId) extSessionIdRef.current = data.sessionId;
      } catch (err) {
        console.warn('[Terview] ext-start failed:', err.message);
      }
      window.postMessage({
        terviewExt: true,
        type: 'EXT_START',
        payload: {
          token: localStorage.getItem('token'),
          preferences: { ...safePreferences, profile, resumeFilename: profileResume },
        }
      }, '*');
    }

    // ── Lane 2: Server Puppeteer (Greenhouse / Lever) ──
    if (serverEnabled) {
      serverLaneActiveRef.current = true;
      lanesLaunched++;
      connectWebSocket();
      await new Promise(r => setTimeout(r, 600));
      addClientLog('🚀 Server lane: launching for ' + Object.keys(serverPlatforms).filter(k => serverPlatforms[k]).join(', '), 'info');
      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({
            profile,
            preferences: { ...preferences, platforms: serverPlatforms },
            resumePath: resumeChoice === 'profile' ? null : resumePath,
            useProfileResume: resumeChoice === 'profile'
          })
        });
        const data = await res.json();
        if (res.ok) {
          setSessionId(data.sessionId);
          addClientLog(`✅ Server session started (ID: ${data.sessionId})`, 'success');
        } else {
          addClientLog(`❌ ${data.message}`, 'error');
          finishServerLane();
        }
      } catch (err) {
        addClientLog(`❌ Server error: ${err.message}`, 'error');
        addClientLog('💡 Make sure your backend server is running with Puppeteer installed', 'warning');
        finishServerLane();
      }
    }

    // If we never kicked off a lane (e.g. extension not installed but only
    // ext-platforms were selected), bail out cleanly.
    if (lanesLaunched === 0) {
      addClientLog('⚠️ No lane was launched. Install the Chrome extension or select Greenhouse/Lever.', 'warning');
      setIsRunning(false);
    }
  };

  const handleStop = async () => {
    // Stop both lanes if they're running. Each is a no-op if its lane isn't active.
    if (extInstalled) {
      window.postMessage({ terviewExt: true, type: 'EXT_STOP' }, '*');
      if (extSessionIdRef.current) {
        try {
          await fetch(`${API_BASE_URL}/api/auto-apply/ext-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ sessionId: extSessionIdRef.current, stats })
          });
        } catch {}
        extSessionIdRef.current = null;
      }
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ action: 'stop' }));
    try { await fetch(`${API_BASE_URL}/api/auto-apply/stop`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }); } catch {}

    setAppliedJobs(prev => prev.map(j => j.status === 'applying' ? { ...j, status: 'failed' } : j));
    extLaneActiveRef.current = false;
    serverLaneActiveRef.current = false;
    setIsRunning(false);
    addClientLog('⏹️ Agent stopped', 'warning');
  };

  const [fieldErrors, setFieldErrors] = useState({});



  const renderStep1 = () => (
    <div className="ajp-form-grid">
      <h3 className="ajp-section-title">🎯 Job Preferences</h3>
      <div className="ajp-row">
        <div className="ajp-field ajp-field-full"><label>Job Titles <span className="ajp-required">*</span></label>
          <input className={fieldErrors.jobTitles ? 'ajp-field-error' : ''} value={preferences.jobTitles} onChange={e => { setPreferences({ ...preferences, jobTitles: e.target.value }); setFieldErrors(f => ({ ...f, jobTitles: false })); }} placeholder="Frontend Developer, React Engineer, UI Developer" />
        </div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Years of Experience</label><input value={profile.yearsExperience} onChange={e => setProfile({ ...profile, yearsExperience: e.target.value })} placeholder="e.g. 5" /></div>
        <div className="ajp-field"><label>Job Type</label>
          <select value={preferences.jobType} onChange={e => setPreferences({ ...preferences, jobType: e.target.value })}>
            <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
          </select>
        </div>
      </div>
      <div className="ajp-field"><label>Locations <span className="ajp-required">*</span></label>
        <input className={fieldErrors.locations ? 'ajp-field-error' : ''} value={preferences.locations} onChange={e => { setPreferences({ ...preferences, locations: e.target.value }); setFieldErrors(f => ({ ...f, locations: false })); }} placeholder="New York, Remote" />
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Remote Preference</label>
          <select value={preferences.remote} onChange={e => setPreferences({ ...preferences, remote: e.target.value })}>
            <option>Any</option><option>Remote Only</option><option>Hybrid</option><option>On-site Only</option>
          </select>
        </div>
        <div className="ajp-field"><label>Max Applications</label>
          <select value={preferences.maxApplications} onChange={e => setPreferences({ ...preferences, maxApplications: e.target.value })}>
            <option>10</option><option>20</option><option>30</option>
          </select>
        </div>
      </div>
      <h3 className="ajp-section-title">🌐 Job Platforms</h3>
      <div className="ajp-platforms">
        {[{ key: 'linkedin', label: 'LinkedIn', icon: '🔷' },
          { key: 'indeed', label: 'Indeed', icon: '🔵' },
          { key: 'dice', label: 'Dice', icon: '🎲' },
          { key: 'monster', label: 'Monster', icon: '👹' },
        ].map(({ key, label, icon }) => (
          <label key={key} className={`ajp-platform-card ${preferences.platforms[key] ? 'enabled' : ''}`}>
            <input type="checkbox" checked={preferences.platforms[key]}
              onChange={() => setPreferences({ ...preferences, platforms: { ...preferences.platforms, [key]: !preferences.platforms[key] } })} />
            <span className="platform-icon">{icon}</span>
            <span className="platform-name">{label}</span>
            {preferences.platforms[key] && <span className="platform-badge">Active</span>}
          </label>
        ))}
      </div>

    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(224,163,255,0.6)', marginBottom: '2px' }}>📄 Resume</div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {profileResume && (
          <button
            type="button"
            onClick={() => { setResumeChoice('profile'); setResumePath(`uploads/resumes/${profileResume}`); setResumeName(profileResume); }}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: '8px', cursor: 'pointer',
              border: `1.5px solid ${resumeChoice === 'profile' ? '#9370db' : 'rgba(255,255,255,0.15)'}`,
              background: resumeChoice === 'profile' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.04)',
              color: 'white', fontSize: '0.78rem', transition: 'all 0.2s', textAlign: 'left'
            }}
          >
            <div style={{ fontWeight: '600' }}>✅ Profile Resume</div>
            <div style={{ fontSize: '0.68rem', opacity: 0.55, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profileResume}</div>
          </button>
        )}
        <button
          type="button"
          onClick={() => { setResumeChoice('upload'); fileInputRef.current?.click(); }}
          style={{
            flex: 1, padding: '7px 10px', borderRadius: '8px', cursor: 'pointer',
            border: `1.5px solid ${resumeChoice === 'upload' ? '#9370db' : 'rgba(255,255,255,0.15)'}`,
            background: resumeChoice === 'upload' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.04)',
            color: 'white', fontSize: '0.78rem', transition: 'all 0.2s', textAlign: 'left'
          }}
        >
          <div style={{ fontWeight: '600' }}>📄 Upload Resume</div>
          <div style={{ fontSize: '0.68rem', opacity: 0.55, marginTop: '2px' }}>{resumeName || 'Choose a file'}</div>
        </button>
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
          onChange={e => handleFileUpload(e.target.files[0])} />
        {/* AI Tailoring toggle — disabled on Lite plan (limit=0). Hint at upgrade. */}
        {(() => {
          const tailoringBlocked = usage?.resumeTailoring?.limit === 0;
          const onClick = tailoringBlocked
            ? () => window.dispatchEvent(new CustomEvent('openPricingModal'))
            : () => setPreferences({ ...preferences, autoTailor: !preferences.autoTailor });
          const enabled = !tailoringBlocked && preferences.autoTailor;
          return (
            <button
              type="button"
              onClick={onClick}
              title={tailoringBlocked ? 'AI Tailoring is available on Pro and Max — click to upgrade' : ''}
              style={{
                flex: 1, padding: '7px 10px', borderRadius: '8px', cursor: 'pointer',
                border: `1.5px solid ${tailoringBlocked ? 'rgba(124,58,237,0.5)' : enabled ? 'rgba(74,222,128,0.5)' : 'rgba(248,113,113,0.4)'}`,
                background: tailoringBlocked ? 'rgba(124,58,237,0.10)' : enabled ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.08)',
                color: 'white', fontSize: '0.78rem', transition: 'all 0.2s', textAlign: 'left',
                opacity: tailoringBlocked ? 0.85 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: '600', color: tailoringBlocked ? '#c4a3ff' : enabled ? '#4ade80' : '#f87171' }}>
                    {tailoringBlocked ? '🔒 AI Tailoring' : '🤖 AI Tailoring'}
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.55, marginTop: '2px' }}>
                    {tailoringBlocked ? 'Upgrade to Pro or Max' : 'Match keywords to job'}
                  </div>
                </div>
                {!tailoringBlocked && (
                  <div style={{
                    width: '32px', height: '18px', borderRadius: '9px', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                    background: enabled ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.15)',
                  }}>
                    <div style={{
                      position: 'absolute', top: '3px', left: enabled ? '17px' : '3px',
                      width: '12px', height: '12px', borderRadius: '50%', transition: 'left 0.2s',
                      background: enabled ? '#4ade80' : 'rgba(255,255,255,0.5)',
                    }} />
                  </div>
                )}
              </div>
            </button>
          );
        })()}
      </div>

    </div>
  );

  const renderStep3 = () => (
    <div className="ajp-form-grid">

      {/* ── Top row: launch/stop + status ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {!isRunning ? (
          <button
            className="ajp-launch-btn"
            onClick={() => preferences.platforms.linkedin ? setShowLinkedInWarning(true) : handleLaunch()}
            disabled={!extInstalled && !canStart}
            style={{ opacity: (extInstalled || canStart) ? 1 : 0.5, cursor: (extInstalled || canStart) ? 'pointer' : 'not-allowed', margin: 0, flexShrink: 0 }}
          >
            {extInstalled ? '⚡ Launch Auto Apply' : '🚀 Launch Auto Apply'}
          </button>
        ) : (
          <button className="ajp-stop-btn" onClick={handleStop} style={{ margin: 0, flexShrink: 0 }}>⏹️ Stop Agent</button>
        )}
        {extInstalled ? (
          <div className="ajp-ext-status-row" style={{ flex: 1, margin: 0 }}>
            <div className="ajp-ext-status-dot" />
            <span style={{ fontSize: '0.78rem' }}>
              {isRunning ? `Applying jobs... (${stats.applied} applied)` : '⚡ Terview Extension ready'}
            </span>
          </div>
        ) : (
          <div className="ajp-ws-status" style={{ flex: 1 }}>
            <div className={`ajp-ws-dot ${wsConnected ? 'connected' : 'disconnected'}`} />
            <span style={{ fontSize: '0.78rem' }}>{wsConnected ? 'Agent connected' : 'Agent disconnected'}</span>
          </div>
        )}
        {sessionId && !isRunning && stats.applied > 0 && (
          <button className="ajp-btn-secondary" onClick={() => navigate('/dashboard')} style={{ flexShrink: 0 }}>
            📊 Dashboard
          </button>
        )}
      </div>

      {/* ── Inline stats bar ── */}
      <div className="ajp-stats-bar">
        <div className="ajp-stats-bar-item ajp-stats-bar--total">
          <span className="ajp-stats-bar-num">{stats.applied + stats.skipped + stats.failed}</span>
          <span className="ajp-stats-bar-label">Total</span>
        </div>
        <div className="ajp-stats-bar-divider" />
        <div className="ajp-stats-bar-item ajp-stats-bar--applied">
          <span className="ajp-stats-bar-num">{stats.applied}</span>
          <span className="ajp-stats-bar-label">Applied</span>
        </div>
        <div className="ajp-stats-bar-divider" />
        <div className="ajp-stats-bar-item ajp-stats-bar--failed">
          <span className="ajp-stats-bar-num">{stats.failed}</span>
          <span className="ajp-stats-bar-label">Failed</span>
        </div>
        <div className="ajp-stats-bar-divider" />
        <div className="ajp-stats-bar-item ajp-stats-bar--skipped">
          <span className="ajp-stats-bar-num">{stats.skipped}</span>
          <span className="ajp-stats-bar-label">Skipped</span>
        </div>
      </div>

      {/* ─── TAB NAV ─── */}
      <div className="ajp-tabs">
        <button
          className={`ajp-tab ${activeTab === 'live' ? 'active' : ''}`}
          onClick={() => setActiveTab('live')}
        >
          <span className="ajp-tab-icon">⚡</span>
          <span className="ajp-tab-text">
            <span className="ajp-tab-title">Live Auto-Apply</span>
            <span className="ajp-tab-sub">LinkedIn · Indeed · Dice · Monster</span>
          </span>
        </button>
        <button
          className={`ajp-tab ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
        >
          <span className="ajp-tab-icon">🌙</span>
          <span className="ajp-tab-text">
            <span className="ajp-tab-title">24/7 Auto-Apply</span>
            <span className="ajp-tab-sub">Greenhouse · Lever · Workable · Ashby</span>
          </span>
          {usage?.applies24_7?.limit > 0 && (
            <span className="ajp-tab-badge">
              {usage.applies24_7.used}/{usage.applies24_7.limit}
            </span>
          )}
        </button>
      </div>

      {/* ─── LIVE TAB ─── */}
      {activeTab === 'live' && (
        <LiveTab
          logs={logs}
          isRunning={isRunning}
          consoleBodyRef={consoleBodyRef}
          consoleEndRef={consoleEndRef}
          appliedJobs={appliedJobs}
          extInstalled={extInstalled}
          user={user}
          preferences={preferences}
          setCanStart={setCanStart}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      {/* ─── 24/7 QUEUE TAB ─── */}
      {activeTab === 'queue' && (
        <QueueTab
          usage={usage}
          resetCountdown={resetCountdown}
          queueJobs={queueJobs}
          queueLoading={queueLoading}
          queueRefreshing={queueRefreshing}
          queueMode={queueMode}
          setQueueMode={setQueueMode}
          queueThreshold={queueThreshold}
          setQueueThreshold={setQueueThreshold}
          queueStatusFilter={queueStatusFilter}
          setQueueStatusFilter={setQueueStatusFilter}
          queueSettingsSavedAt={queueSettingsSavedAt}
          refreshQueue={refreshQueue}
          togglePause={togglePause}
          saveQueueSettings={saveQueueSettings}
          decideJob={decideJob}
          navigate={navigate}
          setActiveTab={setActiveTab}
        />
      )}
    </div>
  );

  return (
    <div className={embedded ? 'auto-apply-embedded-root' : 'neural-glass-homepage'}>
      {!embedded && <div className="neural-background" />}
      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1" /><div className="shape shape-2" /><div className="shape shape-3" /><div className="shape shape-4" />
        </div>
      )}
      {!embedded && <Header setShowAuthModal={setShowAuthModal} />}
      {!embedded && <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />}

      {/* Block navigation while agent is running */}
      {showNavBlockModal && (
        <div className="ajp-nav-block-overlay" onClick={() => setShowNavBlockModal(false)}>
          <div className="ajp-nav-block-modal glass" onClick={e => e.stopPropagation()}>
            <div className="ajp-nav-block-icon">⚠️</div>
            <h3>Agent is still running</h3>
            <p>Leaving this page will not stop the automation. Do you want to stop the agent first?</p>
            <div className="ajp-nav-block-actions">
              <button
                className="ajp-nav-block-stop"
                onClick={async () => {
                  await handleStop();
                  setShowNavBlockModal(false);
                  if (pendingNavUrl) navigate(pendingNavUrl);
                }}
              >
                Stop Agent &amp; Leave
              </button>
              <button className="ajp-nav-block-stay" onClick={() => { setShowNavBlockModal(false); setPendingNavUrl(null); }}>
                Stay on Page
              </button>
            </div>
          </div>
        </div>
      )}

      <main className={`ajp-main ajp-main--wide ${embedded ? 'ajp-embedded' : ''}`}>
        {!embedded && (
          <div className="li-hero">
            <div className="li-hero-badge">🚀 AI-Powered</div>
            <h1 className="li-hero-title">
              AI Auto <span className="li-gradient-text">Job Apply</span>
            </h1>
            <p className="li-hero-subtitle">
              Real browser automation — applies to jobs on your behalf.
            </p>
          </div>
        )}

        <div className="ajp-two-col">
          {/* ── LEFT: job preferences ── */}
          <div className="ajp-left-col glass">
            {renderStep1()}
          </div>

          {/* ── RIGHT: resume + live applications ── */}
          <div className="ajp-right-col glass">
            {renderStep2()}
            <div className="ajp-col-divider" />
            {renderStep3()}
          </div>
        </div>
      </main>
      {showLinkedInWarning && (
        <div className="ajp-modal-overlay" onClick={() => setShowLinkedInWarning(false)}>
          <div className="ajp-modal glass" onClick={e => e.stopPropagation()}>
            <div className="ajp-modal-icon">⚠️</div>
            <h3 className="ajp-modal-title">LinkedIn Automation Warning</h3>
            <p className="ajp-modal-body">
              LinkedIn's Terms of Service prohibit automated job applications. While Terview automates <strong>your own account</strong> on your behalf, LinkedIn may:
            </p>
            <ul className="ajp-modal-list">
              <li>Temporarily restrict or permanently ban your account</li>
              <li>Flag your profile for suspicious activity</li>
            </ul>
            <p className="ajp-modal-body">
              By continuing, you acknowledge that <strong>you use LinkedIn automation at your own risk</strong>. Terview is not liable for any account restrictions.
            </p>
            <div className="ajp-modal-actions">
              <button className="ajp-modal-cancel" onClick={() => setShowLinkedInWarning(false)}>Cancel</button>
              <button className="ajp-modal-confirm" onClick={() => { setShowLinkedInWarning(false); handleLaunch(); }}>I Understand, Continue</button>
            </div>
          </div>
        </div>
      )}

      {!embedded && <Footer />}
    </div>
  );
};

export default AutoJobApply;